import React, {memo, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import {useGetBazzarInventoryListingsQuery, useGetBazzarInventoryOffersQuery} from "store/Global/global.api";
import {formatNumberToEuropeanStyle, INTERACTION, isEmpty, STEPS_STATUS, VIEW_TYPE} from "utils";
import {useActiveItemContext} from "./useActiveItemContext";
import {useActionContext} from "./useActionContext";
import {useAuthorization, useBalance} from "hooks";
import {useModalsContext} from "layouts";

import CountDownBlock from "./CountDownBlock";
import {Icon} from "components/atoms";

import "../style.scss"

const BazaarCurrentItemOffersListings = ({userNftBalance, data}) => {
    const [activeTab, setActiveTab] = useState(VIEW_TYPE.LISTINGS)
    const {chainId} = useActiveItemContext()
    const isFirstRender = useRef(true);
    const {t} = useTranslation()

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        setActiveTab(activeTab === VIEW_TYPE.LISTINGS ? VIEW_TYPE.OFFERS : VIEW_TYPE.LISTINGS)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chainId])

    const isDisabledOfferTab = !data?.details.top_offer

    return (
        <div className="bazaar_current-item_container_offers-lisings">
            <div className="bazaar_current-item_container_offers-lisings_tabs">
                <button
                    onClick={() => setActiveTab(VIEW_TYPE.LISTINGS)}
                    {...(activeTab === VIEW_TYPE.LISTINGS && {className: "active"})}
                >
                    {t("currentListing")}
                </button>
                <button
                    disabled={isDisabledOfferTab}
                    onClick={() => setActiveTab(VIEW_TYPE.OFFERS)}
                    {...(activeTab === VIEW_TYPE.OFFERS && {className: "active"})}
                >
                    {t("currentOffer")}
                </button>
            </div>
            <div className="bazaar_current-item_container_offers-lisings_content">
                {activeTab === VIEW_TYPE.LISTINGS ? (
                    <BazaarCurrentItemListingsTable type={VIEW_TYPE.LISTINGS}/>
                ) : (
                    <BazaarCurrentItemOffersTable userNftBalance={userNftBalance} type={VIEW_TYPE.OFFERS}/>
                )}
            </div>
        </div>
    )
}

export default memo(BazaarCurrentItemOffersListings)


const BazaarCurrentItemOffersTable = ({type, userNftBalance}) => {
    const {isInProgress, data, chainId} = useActiveItemContext();

    return (
        <PaginationTable
            type={type}
            isInProgress={isInProgress}
            TableRowComponent={TableRow}
            tableRowProps={{userNftBalance}}
            queryHook={useGetBazzarInventoryOffersQuery}
            queryParams={{chain_id: chainId.toString(), token_id: data?.details.token_id}}
        />
    );
};

const BazaarCurrentItemListingsTable = ({type}) => {
    const {isInProgress, data, chainId} = useActiveItemContext();

    return (
        <PaginationTable
            type={type}
            tableRowProps={{type}}
            isInProgress={isInProgress}
            TableRowComponent={TableRow}
            queryHook={useGetBazzarInventoryListingsQuery}
            queryParams={{chain_id: chainId.toString(), token_id: data?.details.token_id}}
        />
    );
};

const PaginationTable = (
    {
        type,
        queryHook,
        queryParams,
        isInProgress,
        tableRowProps,
        TableRowComponent,
    }
) => {
    const {actionDetails} = useActionContext()
    const {getKMTBalanceAndSave} = useBalance()
    const {chainId} = useActiveItemContext()
    const contentRef = useRef(null);
    const {t} = useTranslation()
    const [sortConfig, setSortConfig] = useState({
        key: "created_at",
        value: 'DESC'
    });

    const {userData} = useSelector((state) => state.authV2.signIn);

    const [items, setItems] = useState([]);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const {data, isFetching, isLoading, refetch} = queryHook(
        {limit: 6, offset: page * 6, order_by: sortConfig, ...queryParams},
        {skip: isInProgress}
    );

    useEffect(() => {
        handleActionDetails()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actionDetails])

    useEffect(() => {
        const contentElement = contentRef.current;
        if (contentElement) {
            contentElement.addEventListener("scroll", handleScroll);
        }
        return () => {
            if (contentElement) {
                contentElement.removeEventListener("scroll", handleScroll);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasMore, isFetching]);

    useEffect(() => {
        processFetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const resetStates = () => {
        setItems([])
        setPage(0)
        setHasMore(true)
    }

    const handleActionDetails = () => {
        const removalInteractionTypes = [INTERACTION.CANCEL, INTERACTION.BUY, INTERACTION.CANCEL_OFFER, INTERACTION.ACCEPT_OFFER, INTERACTION.CANCEL_LISTING];
        const refetchInteractionTypes = [INTERACTION.LIST, INTERACTION.MAKE_OFFER];
        const editInteractionTypes = [INTERACTION.EDIT, INTERACTION.EDIT_OFFER];

        const isMatchingInteractionType = (types) => types.includes(actionDetails.type) && actionDetails.isSuccess;

        if (isMatchingInteractionType(removalInteractionTypes)) {
            setItems(prevItems => prevItems.filter(item => item.id !== actionDetails.data.order_id));

            if (actionDetails.type === INTERACTION.ACCEPT_OFFER || INTERACTION.BUY) {
                getKMTBalanceAndSave(chainId, userData.wallet)
            }
            return;
        }

        if (isMatchingInteractionType(refetchInteractionTypes)) {
            if ((type === VIEW_TYPE.LISTINGS && actionDetails.type === INTERACTION.LIST) ||
                (type === VIEW_TYPE.OFFERS && actionDetails.type === INTERACTION.MAKE_OFFER)) {
                resetStates()
                refetch();
                return;
            }
        }

        if (isMatchingInteractionType(editInteractionTypes)) {
            setItems(prevItems => prevItems.map(item =>
                item.id === actionDetails.data?.id
                    ? {
                        ...item,
                        order: actionDetails.data?.order,
                        signature: actionDetails.data?.signature,
                        price: actionDetails.data?.price?.toString(),
                        expiration_date: actionDetails.data?.deadline,
                    }
                    : item
            ));
        }
    };

    const handleScroll = () => {
        const contentElement = contentRef.current;

        if (contentElement) {
            const {scrollTop, scrollHeight, clientHeight} = contentElement;

            if (scrollTop + clientHeight >= scrollHeight - 10 && !isFetching && hasMore) {
                setHasMore(false);
                setPage((prevPage) => prevPage + 1);
            }
        }
    };

    const handleSort = (key) => {
        setSortConfig(prevConfig => ({
            key,
            value: prevConfig.key === key && prevConfig.value === 'ASC' ? 'DESC' : 'ASC'
        }));

        resetStates()
        refetch()
    };


    const processFetchData = () => {
        if (data) {
            if (isEmpty(data.orders)) {
                setHasMore(false);
                return
            }

            setItems((prevState) => {
                const filteredData = data.orders.filter(item => !prevState.find(prevItem => item.id === prevItem.id))
                return [...prevState, ...filteredData]
            });
            setHasMore(data.orders.length >= 6);
        }
    }

    const SortIndicator = ({active, direction}) => (
        <span className="sort-indicator">
        {active ? (direction === 'ASC' ? '▲' : '▼') : '⇕'}
    </span>
    );

    const isActiveSort = items.length > 1

    return (
        <div ref={contentRef} className="scrollable-table-container">
            <table className="scrollable-table">
                {(!isLoading || !isFetching || !isEmpty(items)) && (
                    <thead>
                    <tr>
                        <th>{t("from")}</th>
                        <th>{t("quantity")}</th>
                        <th onClick={() => isActiveSort ? handleSort('price') : null}>{t("price")}
                            <SortIndicator
                                active={sortConfig.key === 'price'}
                                direction={sortConfig.value}
                            />
                        </th>
                        <th onClick={() => isActiveSort ? handleSort('expiration_date') : null}>{t("expiry")}
                            <SortIndicator
                                active={sortConfig.key === 'expiration_date'}
                                direction={sortConfig.value}
                            />
                        </th>
                        <th>{t("action")}</th>
                    </tr>
                    </thead>
                )}
                <tbody>
                {!isEmpty(items)
                    ? items.map((item) => <TableRowComponent key={item.id} data={item} {...tableRowProps}/>)
                    : null}
                {(isLoading || isFetching) &&
                    Array(items.length > 5 ? 6 : 5)
                        .fill("")
                        .map((_, rowIndex) => (
                            <tr key={`row-${rowIndex}`}>
                                {Array(5)
                                    .fill("")
                                    .map((_, colIndex) => (
                                        <td key={`cell-${rowIndex}-${colIndex}`}>
                                            <div className="skeleton"></div>
                                        </td>
                                    ))}
                            </tr>
                        ))}
                </tbody>
            </table>
            {!isFetching && !isLoading && isEmpty(items) && <NoData text={t("noResultFound")}/>}
        </div>

    );
};

const NoData = ({text}) => (
    <div className="no-data_container">
        <div className="no-data_container_block">
            <Icon name="noData"/>
            <span>{text}</span>
        </div>
    </div>
)

const TableRow = ({data, type, userNftBalance}) => {
    const {kompeteWethPrice, ethPrice, calculateKompetePrice, handleAuthAndWalletChecks} = useActiveItemContext()
    const {setCurrentModal} = useModalsContext()
    const {isAuth} = useAuthorization();
    const {t} = useTranslation()

    const {userData} = useSelector((state) => state.authV2.signIn);

    const isPriceDetailsAvailable = kompeteWethPrice !== null && ethPrice !== null
    const isListingTable = type === VIEW_TYPE.LISTINGS
    const isUserCanAccept = isListingTable ? true : userNftBalance >= data.order.amount

    const cancel = () => {
        if (!handleAuthAndWalletChecks()) return

        const status = isListingTable ? STEPS_STATUS.CANCEL_LISTING : STEPS_STATUS.CANCEL_OFFER

        setCurrentModal({status, data})
    }

    const buy = () => {
        if (!handleAuthAndWalletChecks()) return

        if (isListingTable) {
            const itemInfo = {
                name: data.inventory.name,
                priceUSD: calculateKompetePrice(data.price),
                tokenId: data.inventory.token_id,
                listing: {
                    id: data.id,
                    chain: data.chain,
                    price: data.price,
                    order: data.order,
                    signature: data.signature,
                },
            }

            setCurrentModal({status: STEPS_STATUS.CONFIRM_PURCHASE, data: itemInfo})
        } else {
            setCurrentModal({
                status: STEPS_STATUS.ACCEPT_OFFER, data: {
                    ...data,
                    priceUSD: calculateKompetePrice(data.price),
                }
            })
        }
    }

    const isOwner = userData.id === data.user.user_uuid;
    const buttonText = isOwner ? "cancel" : isListingTable ? "buy" : "accept";
    const buttonClick = isOwner ? () => cancel() : () => buy();
    const buttonClass = isOwner ? "own" : "default";
    const isButtonDisabled = !isPriceDetailsAvailable || ((buttonText === "accept") ? !isUserCanAccept : false)

    const renderUserDisplayName = () =>
        isAuth ? (isOwner ? "You" : data.user.nickname) : data.user.nickname;

    return (
        <tr>
            <td>{renderUserDisplayName()}</td>
            <td>{isListingTable ? 1 : data.order.amount}</td>
            <td>{formatNumberToEuropeanStyle(data.price)}</td>
            <td>
                <CountDownBlock expirationDate={data.expiration_date}/>
            </td>
            <td>
                <button
                    onClick={buttonClick}
                    className={buttonClass}
                    disabled={isButtonDisabled}
                >
                    {t(buttonText)}
                </button>
            </td>
        </tr>
    );
};

